import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
  Settings, Save, RefreshCw, Clock, Calendar, Bell, ExternalLink, Users, Link
} from 'lucide-react';

export default function AdminBookingSettingsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    default_buffer_time: 15,
    default_durations: [15, 30, 45, 60],
    reminder_1_day: true,
    reminder_1_hour: true,
    booking_advance_days: 30,
    min_notice_hours: 2
  });
  const [bookingPages, setBookingPages] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, pagesRes] = await Promise.all([
        api.get('/api/admin/booking-settings'),
        api.get('/api/staff/booking-pages')
      ]);
      setSettings(settingsRes.data.settings);
      setBookingPages(pagesRes.data.booking_pages || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/booking-settings', settings);
      toast.success('Booking settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleDuration = (duration) => {
    setSettings(prev => ({
      ...prev,
      default_durations: prev.default_durations.includes(duration)
        ? prev.default_durations.filter(d => d !== duration)
        : [...prev.default_durations, duration].sort((a, b) => a - b)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#84CC16]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-booking-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Booking Settings</h1>
          <p className="text-gray-500">Configure global settings for the booking system</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#84CC16] hover:bg-[#65a30d]">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Settings Cards */}
      <div className="grid grid-cols-2 gap-6">
        {/* Appointment Durations */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0A1628]">Appointment Durations</h3>
              <p className="text-sm text-gray-500">Available meeting lengths</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {[15, 30, 45, 60].map(d => (
              <button
                key={d}
                onClick={() => toggleDuration(d)}
                className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                  settings.default_durations.includes(d)
                    ? 'bg-[#84CC16] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {d} min
              </button>
            ))}
          </div>
        </div>

        {/* Buffer Time */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0A1628]">Default Buffer Time</h3>
              <p className="text-sm text-gray-500">Time between appointments</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {[15, 30, 45, 60].map(b => (
              <button
                key={b}
                onClick={() => setSettings({ ...settings, default_buffer_time: b })}
                className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                  settings.default_buffer_time === b
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {b} min
              </button>
            ))}
          </div>
        </div>

        {/* Booking Window */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0A1628]">Booking Window</h3>
              <p className="text-sm text-gray-500">How far in advance can book</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label>Advance Days</Label>
              <Input
                type="number"
                value={settings.booking_advance_days}
                onChange={(e) => setSettings({ ...settings, booking_advance_days: parseInt(e.target.value) || 30 })}
                min="1"
                max="365"
                className="mt-1 w-32"
              />
              <p className="text-xs text-gray-500 mt-1">Max days ahead clients can book</p>
            </div>
            <div>
              <Label>Minimum Notice (Hours)</Label>
              <Input
                type="number"
                value={settings.min_notice_hours}
                onChange={(e) => setSettings({ ...settings, min_notice_hours: parseInt(e.target.value) || 2 })}
                min="1"
                max="72"
                className="mt-1 w-32"
              />
              <p className="text-xs text-gray-500 mt-1">Min hours before appointment</p>
            </div>
          </div>
        </div>

        {/* Email Reminders */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0A1628]">Email Reminders</h3>
              <p className="text-sm text-gray-500">Automatic reminder emails</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">1 Day Before</p>
                <p className="text-sm text-gray-500">Send reminder 24 hours before</p>
              </div>
              <Switch
                checked={settings.reminder_1_day}
                onCheckedChange={(checked) => setSettings({ ...settings, reminder_1_day: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">1 Hour Before</p>
                <p className="text-sm text-gray-500">Send reminder 1 hour before</p>
              </div>
              <Switch
                checked={settings.reminder_1_hour}
                onCheckedChange={(checked) => setSettings({ ...settings, reminder_1_hour: checked })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Staff Booking Pages */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-[#0A1628]">Staff Booking Pages</h3>
            <p className="text-sm text-gray-500">All active booking pages for staff members</p>
          </div>
        </div>

        {bookingPages.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Link className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No booking pages set up yet</p>
            <p className="text-sm">Staff can set up their availability in their profile</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bookingPages.map((page) => (
              <div
                key={page.user_id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
              >
                <div>
                  <p className="font-medium text-[#0A1628]">{page.staff_name}</p>
                  <p className="text-sm text-gray-500 font-mono">/booking/{page.slug}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    page.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {page.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <a
                    href={`/booking/${page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-500" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-[#84CC16]/5 rounded-xl p-4 border border-[#84CC16]/20">
        <h4 className="font-medium text-[#0A1628] mb-2">How Booking Works</h4>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• Each staff member sets their availability in their Profile under "Booking Schedule"</li>
          <li>• Public booking pages are at /booking/firstname-lastname</li>
          <li>• Clients select duration, date, time and fill out their info</li>
          <li>• Both staff and client receive confirmation emails</li>
          <li>• Automatic reminders sent 1 day and 1 hour before meetings</li>
          <li>• Staff can view and manage bookings in the Bookings section</li>
        </ul>
      </div>
    </div>
  );
}
